import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { TextField, Typography, MenuItem } from '@mui/material';
import CustomDatePickerRange from '@src/components/CustomDatePickerRange';
import {
  MERGE_PRODUCT_TYPE,
  ORDER_STATUS,
  PACKAGE_LEVEL,
  PACKAGE_STATUS,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { LANGUAGE, PAGINATION_LIMIT } from '@src/constants';
import Table from '@src/components/Table';
import apis from '@src/apis';
import i18n from '@src/languages';
import { delimitNumber } from '@src/utils/number';
// import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import actions from '@src/redux/actions';
import RefreshBtn from '@src/assets/images/refresh-btn.svg';
import RefreshBtnActive from '@src/assets/images/refresh-btn-active.svg';
import smallLogoTts from '@src/assets/logo/small-logo.svg';
import smallLogoApi from '@src/assets/logo/small-logo-api.svg';
import smallLogoDubbing from '@src/assets/logo/small-logo-dubbing.svg';
import ProcessHandler from '@src/components/ProcessHandler';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';
import { checkCustomPackage } from '@src/services/package';
import { StyledChip, StyledPaymentHistory } from './index.style';

const initialFilter = {
  packageLevel: '',
  packageStatus: '',
  type: '',
  createdAt: [null, null],
};

// const filterStudioPackageLevels = [
//   PACKAGE_LEVEL.STANDARD,
//   PACKAGE_LEVEL.PROFESSIONAL,
//   PACKAGE_LEVEL.SPECIAL,
// ];

// const filterDubbingPackageLevels = [
//   PACKAGE_LEVEL.STANDARD,
//   PACKAGE_LEVEL.PROFESSIONAL,
//   PACKAGE_LEVEL.SPECIAL,
// ];

const mergeProductType = [
  MERGE_PRODUCT_TYPE.aivoiceStudio,
  MERGE_PRODUCT_TYPE.aivoiceApi,
];

const PaymentHistory = ({ open = true }) => {
  const dispatch = useDispatch();

  const [orders, setOrders] = useState([]);
  const [paging, setPaging] = useState({ page: 1, total: 0 });
  const location = useLocation();
  // Other pages can open this one pre-filtered, e.g. the top-paid event's orders.
  const [filter, setFilter] = useState(() => ({
    ...initialFilter,
    ...location.state?.paymentHistoryFilter,
  }));
  const [sort, setSort] = useState('createdAt_desc');
  const [isActiveReset, setActiveReset] = useState(false);
  const [loading, setLoading] = useState(false);

  const { usingPackage, fetchPaymentHistory, usingDubbingPackage } =
    useSelector((state) => state.user);
  const { t } = useTranslation();
  const { language } = i18n;

  const handleChangePage = (newPage) => {
    setPaging({ ...paging, page: newPage });
  };

  const handleSetTotal = (total) => {
    setPaging({ ...paging, total });
  };

  const handleChangeFilter = (event) => {
    const { name, value } = event.target;
    setFilter({ ...filter, [name]: value });
    handleChangePage(1);
  };

  const handleResetFilter = () => {
    setFilter(initialFilter);
    handleChangePage(1);
  };

  const handleChangeSort = (newSort) => {
    setSort(newSort);
    handleChangePage(1);
  };

  const handleChangeDatePickerRange = (value) => {
    setFilter({ ...filter, createdAt: value });
    handleChangePage(1);
  };

  const listProductTypes = () => mergeProductType;

  // STANDARD package level is PRO level, special package level is ENTERPRISE level
  const mapPackageLevelValue = (value) => {
    switch (value) {
      case PACKAGE_LEVEL.STANDARD:
        return PACKAGE_LEVEL.PRO;
      case PACKAGE_LEVEL.SPECIAL:
        return PACKAGE_LEVEL.ENTERPRISE;
      default:
        return value;
    }
  };

  const renderTypography = (content, typeText) => (
    <Typography className={`text-${typeText}-body-cell`}>
      {content === 'null' || content === 'undefined' ? '' : content}
    </Typography>
  );

  const renderMoneyTypography = (content, typeText) => (
    <Typography className={`text-${typeText}-body-cell`}>
      <span className="vnd">đ</span>
      {content}
    </Typography>
  );

  const renderTypographyPkgName = (content, typeText, renewalType) => (
    <div className="pkgNameWrapper">
      <Typography className={`text-${typeText}-body-cell`}>
        {content}
      </Typography>
      {renewalType === 'AUTO_RENEW' && (
        <div className="typeOfExpire">
          {renewalType === 'AUTO_RENEW' ? t('autoRenewal') : ''}
        </div>
      )}
    </div>
  );

  const getPackageStatusText = (status) => {
    switch (status) {
      case PACKAGE_STATUS.PAID:
        return 'paid';
      case PACKAGE_STATUS.PENDING:
        return 'pending';
      case PACKAGE_STATUS.CANCEL:
        return 'canceled';
      default:
        return '';
    }
  };

  const getOrderStatusText = (status) => {
    switch (status) {
      case ORDER_STATUS.PAID:
      case ORDER_STATUS.WAITING_FOR_ACTIVATION:
        return 'paid';
      case PACKAGE_STATUS.PENDING:
        return 'pending';
      case PACKAGE_STATUS.CANCEL:
        return 'canceled';
      default:
        return '';
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    const { packageLevel, packageStatus, createdAt, type } = filter;

    // If packageLevel is PRO, we need to change it to STANDARD because standard package is pro level
    const packageLevelValue = mapPackageLevelValue(packageLevel);
    const paramsToGetOrders = {
      packageLevel: packageLevelValue || undefined,
      status: packageStatus || undefined,
      startDate: createdAt[0]
        ? moment(createdAt[0]).startOf('day').toISOString()
        : undefined,
      endDate: createdAt[1]
        ? moment(createdAt[1]).endOf('day').toISOString()
        : undefined,
      sort,
      offset: (paging.page - 1) * PAGINATION_LIMIT,
    };
    if (type === MERGE_PRODUCT_TYPE.aivoiceStudio) {
      paramsToGetOrders['type[$in]'] = [
        PACKAGE_TYPE.STUDIO,
        PACKAGE_TYPE.DUBBING,
      ];
    } else if (type === MERGE_PRODUCT_TYPE.aivoiceApi) {
      paramsToGetOrders.type = PACKAGE_TYPE.API;
    } else {
      paramsToGetOrders.type = type || undefined;
    }
    const data = await apis.orders.getOrders(paramsToGetOrders);
    if (data && data.status) {
      setOrders(data.result.orders);
      handleSetTotal(data.result.total);
      setLoading(false);
    }
  };

  const renderRemainingTime = (expiredIn) => {
    if (expiredIn < 30) return '';
    if (expiredIn === 30) return t('packageDurationMonth');
    if (expiredIn === 90) return t('quarterly');
    if (expiredIn === 360) return t('packageDurationYear');
    if (expiredIn === 365) return t('packageDurationYear');
    return t('packageDurationYear');
  };

  const renderPackageName = (pkg) => {
    const isApiV3Pkg =
      pkg.type === PACKAGE_TYPE.API &&
      pkg?.version?.includes(PACKAGE_STUDIO_VERSION.V3);
    const isPackageCustom = checkCustomPackage({
      level: pkg.level,
      type: pkg.type,
    });
    const notShowDuration = isApiV3Pkg || isPackageCustom;

    const packageName = pkg?.name?.[language] || pkg?.name?.[LANGUAGE.EN] || '';
    const duration = notShowDuration ? '' : renderRemainingTime(pkg?.expiresIn);
    const separator = duration && !notShowDuration ? ' - ' : '';

    let pkgName = `${packageName}${separator}${duration}`;
    if (isApiV3Pkg) pkgName = `API - ${pkgName}`;

    return pkgName;
  };

  const renderOrderStatus = (status) => {
    let color;
    switch (status) {
      case ORDER_STATUS.PAID:
      case ORDER_STATUS.WAITING_FOR_ACTIVATION:
        color = 'success';
        break;
      case ORDER_STATUS.CANCEL:
        color = 'error';
        break;
      case ORDER_STATUS.PENDING:
        color = 'info';
        break;
      default:
        break;
    }
    return (
      <StyledChip color={color}>{t(getOrderStatusText(status))}</StyledChip>
    );
  };

  const renderLogoProd = (prodType) => {
    switch (prodType) {
      case PACKAGE_TYPE.STUDIO:
        return smallLogoTts;
      case PACKAGE_TYPE.API:
        return smallLogoApi;
      case PACKAGE_TYPE.DUBBING:
        return smallLogoDubbing;
      default:
        return smallLogoTts;
    }
  };

  useEffect(() => {
    if ((open && !orders.length) || orders.length) {
      fetchOrders();
      if (fetchPaymentHistory)
        dispatch(actions.user.fetchPaymentHistory(false));
    }
  }, [
    open,
    sort,
    paging.page,
    filter,
    usingPackage,
    fetchPaymentHistory,
    usingDubbingPackage,
  ]);

  const renderPaymentMethodName = (row) => {
    const isAutoRenew =
      row?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;
    const paymentMethod = row?.paymentMethod;

    const name = isAutoRenew
      ? paymentMethod?.autoRenewName
      : paymentMethod?.name;

    const paymentMethodName = name?.[language] || name?.[LANGUAGE.EN];

    const fallbackName =
      paymentMethod?.name?.[language] || paymentMethod?.name?.[LANGUAGE.EN];

    return renderTypography(paymentMethodName || fallbackName, 'light');
  };

  useEffect(() => {
    if (
      filter.type ||
      // filter.packageLevel ||
      filter.packageStatus ||
      filter.createdAt[0]
    )
      setActiveReset(true);
    else setActiveReset(false);
  }, [filter]);

  const columns = [
    {
      field: 'createdAt',
      title: t('transactionTime'),
      sortable: false,
      align: 'left',
      render: (row) =>
        renderTypography(
          moment(row.createdAt).format('DD/MM/YYYY - HH:mm'),
          'light',
        ),
    },
    {
      field: '',
      title: '',
      sortable: false,
      align: 'right',
      render: (row) => (
        <img src={renderLogoProd(row?.type)} alt="small-logo-tts" />
      ),
    },
    {
      field: `package.name.${language}`,
      title: t('packageName'),
      sortable: false,
      align: 'left',
      render: (row) =>
        renderTypographyPkgName(
          renderPackageName(row?.package),
          'light',
          row?.renewalType,
        ),
    },
    {
      field: `paymentMethod.name.${language}`,
      title: t('paymentMethod'),
      sortable: false,
      align: 'left',
      render: (row) => renderPaymentMethodName(row),
    },
    {
      field: 'packagePrice',
      title: t('price'),
      sortable: false,
      align: 'right',
      render: (row) =>
        renderMoneyTypography(delimitNumber(row.price, language), 'light'),
    },
    {
      field: 'status',
      title: t('transactionStatus'),
      sortable: false,
      align: 'center',
      render: (row) => renderOrderStatus(row.status, row.paymentMethod?.type),
    },
  ];

  return (
    <StyledPaymentHistory>
      <div className="header-payment">
        <Typography className="title">{t('paymentHistory')}</Typography>
      </div>

      <div className="table-wrapper">
        <div className="filter-wrapper">
          <TextField
            size="small"
            className="text-field"
            variant="outlined"
            value={filter.type}
            name="type"
            select
            label={t('selectPackageName')}
            onChange={handleChangeFilter}
          >
            {listProductTypes().map((item) => (
              <MenuItem
                data-id="filter-by-product"
                data-option-product={item}
                value={item}
              >
                {t(item)}
              </MenuItem>
            ))}
          </TextField>
          {/* <TextField
            size="small"
            className="text-field"
            variant="outlined"
            value={filter.packageLevel}
            name="packageLevel"
            select
            label={t('packageName')}
            onChange={handleChangeFilter}
          >
            {filterPackageLevels.map((item) => (
              <MenuItem key={item} value={item}>
                {t(item)}
              </MenuItem>
            ))}
          </TextField> */}
          <TextField
            size="small"
            className="text-field"
            variant="outlined"
            value={filter.packageStatus}
            name="packageStatus"
            select
            label={t('chooseStatus')}
            onChange={handleChangeFilter}
          >
            {Object.keys(ORDER_STATUS).map((item) => (
              <MenuItem
                data-id="filter-by-status-order"
                data-option-status={item}
                key={item}
                value={PACKAGE_STATUS[item]}
              >
                {t(getPackageStatusText(item))}
              </MenuItem>
            ))}
          </TextField>
          <div
            data-id="filter-by-day-payment"
            data-current-package-code={usingPackage?.code}
          >
            <CustomDatePickerRange
              value={filter.createdAt}
              onChange={handleChangeDatePickerRange}
            />
          </div>
          <img
            data-id="reset-filter-payment-history"
            data-current-package-code={usingPackage?.code}
            src={isActiveReset ? RefreshBtnActive : RefreshBtn}
            alt="refresh"
            onClick={handleResetFilter}
            className="refresh-icon"
          />
        </div>
        <ProcessHandler loading={loading} mt="30px" mb="30px" align="center">
          <Table
            columns={columns}
            data={orders}
            total={paging.total}
            page={paging.page}
            sort={sort}
            onChangePage={handleChangePage}
            onChangeSort={handleChangeSort}
            headerStyle={{ height: 48 }}
          />
        </ProcessHandler>
      </div>
    </StyledPaymentHistory>
  );
};

export default PaymentHistory;
